import React, { useState } from 'react';
import { MailIcon } from 'lucide-react';
import { contact } from '../data/site';
type Status = 'idle' | 'error' | 'opened';
const fieldClass = 'w-full border border-line bg-paper px-4 py-3 text-[14px] text-ink placeholder:text-ink-50/70 focus:border-green focus:outline-none';
type FormValues = {
  name: string;
  company: string;
  email: string;
  message: string;
};
const emptyValues: FormValues = {
  name: '',
  company: '',
  email: '',
  message: ''
};
export function buildMailto(values: FormValues): string {
  const subject = `Advisory enquiry — ${values.company.trim() || values.name.trim() || 'Website contact'}`;
  const body = ['Hello Premier Advisory Services,', '', values.message.trim(), '', '—', `Name: ${values.name.trim()}`, `Company: ${values.company.trim() || '(not provided)'}`, `Email: ${values.email.trim()}`, '', 'Sent from premieradvisory.co.ke'].join('\n');
  return `mailto:${contact.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}
export function ContactForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [values, setValues] = useState<FormValues>(emptyValues);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setValues(v => ({
      ...v,
      [e.target.name]: e.target.value
    }));
    if (status === 'error') setStatus('idle');
  };
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!values.name.trim() || !values.email.trim() || !values.message.trim()) {
      setStatus('error');
      return;
    }
    window.location.href = buildMailto(values);
    setStatus('opened');
  };
  return <div className="h-fit border border-line bg-white p-8" data-id="element-74" data-mp="3kn4zj1tnr7mb">
      <h3 className="font-display text-[24px] text-ink" data-id="element-75" data-mp="vu9hxr51wcrw">Need help with your books?</h3>
      <p className="mt-3 text-[14px] leading-relaxed text-ink-70" data-id="element-76" data-mp="vwlxxyr90d2b">
        Fill this in and we&apos;ll open a pre-written email to{' '}
        <span className="font-mono text-[13px] text-green-dark" data-id="element-77" data-mp="2wvpjlimdb5ko">{contact.email}</span>{' '}
        in your mail app — add anything missing, then send.
      </p>

      <form onSubmit={handleSubmit} className="mt-7 space-y-4" noValidate data-id="element-78" data-mp="2rzqo7b2mgnar">
        <div data-id="element-79" data-mp="35wdzkivbng07">
          <label htmlFor="name" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.12em] text-ink-50" data-id="element-80" data-mp="35v2t2bahru5e">
            Name
          </label>
          <input id="name" name="name" value={values.name} onChange={handleChange} className={fieldClass} placeholder="Your full name" autoComplete="name" required data-id="element-81" data-mp="2d8ro6ofur72s" />
        </div>
        <div data-id="element-82" data-mp="2i61kmo1nsj04">
          <label htmlFor="company" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.12em] text-ink-50" data-id="element-83" data-mp="2i5hzyzkmjgie">
            Company
          </label>
          <input id="company" name="company" value={values.company} onChange={handleChange} className={fieldClass} placeholder="Organisation (optional)" autoComplete="organization" data-id="element-84" data-mp="1pinj0cn0mz9o" />
        </div>
        <div data-id="element-85" data-mp="3s75mm397sx5g">
          <label htmlFor="email" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.12em] text-ink-50" data-id="element-86" data-mp="3s3hrizo4g2om">
            Email
          </label>
          <input id="email" name="email" type="email" value={values.email} onChange={handleChange} className={fieldClass} placeholder="you@company.co.ke" autoComplete="email" required data-id="element-87" data-mp="opndlshphk3g" />
        </div>
        <div data-id="element-88" data-mp="jrlwve7r5wv5">
          <label htmlFor="message" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.12em] text-ink-50" data-id="element-89" data-mp="2zgx1c2jdtwmn">
            Message
          </label>
          <textarea id="message" name="message" rows={4} value={values.message} onChange={handleChange} className={`${fieldClass} resize-none`} placeholder="What do you need help with?" required data-id="element-90" data-mp="3jpn298lhajg3" />
        </div>

        {status === 'error' && <p role="alert" className="font-mono text-[11px] text-brandred" data-id="element-91" data-mp="35zjl2w1ghfxi">
            Please complete your name, email and message.
          </p>}

        <button type="submit" className="flex w-full items-center justify-center gap-2 bg-green px-6 py-4 font-mono text-[11px] uppercase tracking-[0.1em] text-ink transition-colors hover:bg-green-dark hover:text-white" data-id="element-92" data-mp="2ht2p3d19fp2">
          <MailIcon className="h-4 w-4" aria-hidden="true" data-id="element-93" data-mp="3oo839a8k175l" />
          Open Pre-filled Email
        </button>

        <p aria-live="polite" className="font-mono text-[11px] text-green-dark" data-id="element-94" data-mp="2i9z3ohkyaqqc">
          {status === 'opened' ? 'Your mail app should now be open with the message drafted — review and press send.' : ''}
        </p>

        {status === 'opened' && <p className="text-[12px] leading-relaxed text-ink-50" data-id="element-95" data-mp="285iq5uuy1lpp">
            Nothing opened? Email us directly at{' '}
            <a href={`mailto:${contact.email}`} className="text-green-dark underline" data-id="element-96" data-mp="2d2iv22g24if8">
              {contact.email}
            </a>{' '}
            or call {contact.phonePrimary}.
          </p>}
      </form>
    </div>;
}